import axios from "axios";
import {
  createSessionTokenCustom,
  getOcrInterfaceSessionToken,
} from "services/interface";
import { updateGblParams } from "services/setting";

const AI_SESSION_MINUTES = 99999999;
export const DEFAULT_AI_BASE_URL =
  process.env.NODE_ENV === "development" ||
  window.location.hostname === "dev.carmen4.com"
    ? "https://carmen-ocr-backend-xntb.onrender.com/api/v1/carmen"
    : "https://carmen-ocr-backend.onrender.com/api/v1/carmen";

let aiAuthorization = "";

function extractAuthorization(payload, depth = 0) {
  if (typeof payload === "string") return depth === 0 ? payload.trim() : "";
  if (!payload || typeof payload !== "object" || depth > 3) return "";

  const authorizationEntry = Object.entries(payload).find(
    ([key, value]) =>
      key.toLowerCase() === "authorization" && typeof value === "string",
  );
  if (authorizationEntry) return authorizationEntry[1].trim();

  const wrapperEntry = Object.entries(payload).find(
    ([key, value]) =>
      ["data", "result", "response", "payload"].includes(key.toLowerCase()) &&
      typeof value === "string",
  );
  if (wrapperEntry) return wrapperEntry[1].trim();

  for (const value of Object.values(payload).filter(
    (item) => item && typeof item === "object",
  )) {
    const authorization = extractAuthorization(value, depth + 1);
    if (authorization) return authorization;
  }
  return "";
}

const aiApi = axios.create({
  baseURL: DEFAULT_AI_BASE_URL,
  timeout: 30000,
});

export function getAiFrontendUrl() {
  return process.env.NODE_ENV === "development" ||
    window.location.hostname === "dev.carmen4.com"
    ? "https://ocr-tau-five.vercel.app"
    : "https://app.carmen.blue";
}

export async function getAiBaseUrl() {
  return DEFAULT_AI_BASE_URL;
}

aiApi.interceptors.request.use(async (config) => ({
  ...config,
  baseURL: await getAiBaseUrl(),
  headers: {
    ...config.headers,
    Authorization: aiAuthorization || localStorage.getItem("AccessToken") || "",
  },
}));

async function createAiDirectAuthorization() {
  const session = await createSessionTokenCustom(AI_SESSION_MINUTES);
  const token = extractAuthorization(session);
  if (!token) {
    throw new Error("Cannot create Carmen session token.");
  }
  return token;
}

export async function persistAiAuthorization(token) {
  if (!token) throw new Error("Cannot create Carmen session token.");
  await updateGblParams({
    Sections: "Setup",
    KeyField: "AI_Token",
    DataValue: token,
    UserModified: localStorage.getItem("UserName"),
  });
}

function getSelectedCarmenApiUrl() {
  const configuredUrl = window.config.url;
  if (configuredUrl) return configuredUrl;

  const adminToken = localStorage.getItem("adminToken");
  const selectedCompany = window.config.arrCompany?.find(
    (company) => company.adminToken === adminToken,
  );
  return selectedCompany?.apiUrl || window.config.apiUrl;
}

function getCarmenOrigin() {
  const apiUrl = getSelectedCarmenApiUrl();
  try {
    return new URL(apiUrl).origin;
  } catch (error) {
    return apiUrl || "";
  }
}

export function getAiSettingContext() {
  return {
    uri: getCarmenOrigin(),
    bu: localStorage.getItem("Tenant") || "",
  };
}

async function requestAiNotificationStatus() {
  const { data } = await aiApi.get("/notifications", {
    params: getAiSettingContext(),
  });

  if (data === true) return true;
  return Boolean(
    data &&
    typeof data === "object" &&
    Object.values(data).some((value) => value === true),
  );
}

export async function getAiNotificationStatus() {
  const ocrSession = await getOcrInterfaceSessionToken();
  aiAuthorization = extractAuthorization(ocrSession);
  if (!aiAuthorization) {
    throw new Error("Cannot create OCR interface session token.");
  }

  let notificationStatus;
  try {
    notificationStatus = await requestAiNotificationStatus();
  } catch (error) {
    if (error?.response?.status !== 401) throw error;

    aiAuthorization = await createAiDirectAuthorization();
    notificationStatus = await requestAiNotificationStatus();
  }

  await persistAiAuthorization(aiAuthorization);
  return notificationStatus;
}

async function requestPmsSettings(pms, autoPost, createKey = false) {
  const session = await createSessionTokenCustom(9999999);
  const authorization = extractAuthorization(session);
  if (!authorization) throw new Error("Cannot create Carmen session token.");
  const baseURL = await getAiBaseUrl();
  const context = {
    uri:
      window.location.origin === "http://localhost:3000"
        ? "https://dev.carmen4.com"
        : window.location.origin,
    bu: localStorage.getItem("Tenant") || "",
    pms,
  };
  const config = { headers: { Authorization: authorization }, timeout: 30000 };
  const url = baseURL + "/pms/" + (createKey ? "keys" : "settings");
  const response = createKey
    ? await axios.post(url, context, config)
    : autoPost === undefined
      ? await axios.get(url, { ...config, params: context })
      : await axios.put(url, { ...context, auto_post: autoPost }, config);
  return response.data;
}

export function getAiPmsSettings(pms) {
  return requestPmsSettings(pms);
}

export function updateAiPmsSettings(pms, autoPost) {
  return requestPmsSettings(pms, autoPost);
}

export function createAiPmsKey(pms) {
  return requestPmsSettings(pms, undefined, true);
}
